import { useEffect, useState } from 'react'

import { Button, Form, Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import { Calendar } from 'react-feather'
import callAPI from '../../helpers/callAPI'
import useCallApi from '../../hooks/useCallApi'
import Affiliate from '../../templates/Affiliate/Affiliate'
import Campaign from '../../templates/Campaign/Campaign'
import CustomButton from '../../templates/UI/CustomButton/CustomButton'
import AffiliateSearch from '../AffiliateSearch/AffiliateSearch'
import CampaignSearch from '../CampaignSearch/CampaignSearch'
import calculateAffiliateRate from '../../helpers/calculateAffRate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import ScheduledRateChangeBanner from '../../pages/Campaign/CampaignSettings/Edit/Rates/ScheduledRateChangeBanner/ScheduledRateChangeBanner'

const SpecialRatesModal = ({ setIsOpen, refetch, affiliate, campaign, ratename, showSystemMessage, ratePitchId }) => {
    const [formData, setFormData] = useState({
        campaign: {
            name: '',
            id: campaign.id ? campaign.id : 0,
        },
        affiliate: {
            id: affiliate.id ? affiliate.id : 0,
        },
        affiliate_rate: '',
        network_rate: '',
        display_rate: '',
        display_net_rate: '',
        display_rate_type: '',
        base_rate_type: '',
        hide_sale_amount: false,
        override_url: '',
        rate_names: [],
        rate_name: ratename,
        rate_pitch_id: ratePitchId && ratePitchId > 0 ? parseInt(ratePitchId) : null,
    })

    const { data: ratenameData, dataLoaded: ratenameDataLoaded } = useCallApi(`affiliate/${affiliate.id ? affiliate.id : formData.affiliate.id}/${campaign.id ? campaign.id : formData.campaign.id}/specialRates/rateNames`, 'GET', {}, [formData.campaign, formData.affiliate])

    const [submitting, setSubmitting] = useState(false)
    const [isScheduleMode, setIsScheduleMode] = useState(false)
    const [scheduleDate, setScheduleDate] = useState(null)
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false)
    const [pendingScheduledRateChange, setPendingScheduledRateChange] = useState(null)

    const saveRate = async (e) => {
        setSubmitting(true)
        e.preventDefault()

        if ((!ratename || ratename === '') && formData.rate_names.length < 1) {
            showSystemMessage('error', 'Rate name must be selected')
            setSubmitting(false)
            return
        }

        const res = await callAPI(affiliate.id ? `affiliate/${affiliate.id}/specialRates` : `campaign/${campaign.id}/specialRates`, ratename ? 'PUT' : 'POST', formData)
        if (res.success) {
            setSubmitting(false)
            setIsOpen(false)
            //showSystemMessage('success', 'Special rate saved.')
            refetch()
        } else if (!res.success) {
            setSubmitting(false)
            showSystemMessage('error', res.errors.message)
        } else {
            showSystemMessage('error', res.errors.message)
            setSubmitting(false)
        }
    }

    const closeModal = () => {
        setIsScheduleMode(false)
        setScheduleDate(null)
        setScheduleSubmitting(false)
        setPendingScheduledRateChange(null)
        setIsOpen(false)
    }

    const handleScheduleSpecialRateChange = async () => {
        if (!ratename) {
            showSystemMessage('error', 'Rate name must be selected')
            return
        }
        if (!formData.affiliate?.id || !formData.campaign?.id) {
            showSystemMessage('error', 'Affiliate and campaign must be selected.')
            return
        }
        if (!scheduleDate) {
            showSystemMessage('error', 'Please select a date and time')
            return
        }

        const dr = parseFloat(formData.display_rate)
        const dnr = parseFloat(formData.display_net_rate)
        if (dr > 0 && (!formData.display_rate_type || formData.display_rate_type === '')) {
            showSystemMessage('error', 'If you are setting a display rate, you must select a display rate type.')
            return
        }
        if (dr > 0 && (formData.display_net_rate === '' || formData.display_net_rate == null || isNaN(dnr))) {
            showSystemMessage('error', 'If you are setting a display rate, you need to set the display net rate.')
            return
        }

        setScheduleSubmitting(true)
        const body = {
            date: moment(scheduleDate).format('YYYY-MM-DD HH:mm:ss'),
            affiliate: { id: formData.affiliate.id },
            campaign: { id: formData.campaign.id },
            rate_name: ratename,
            affiliate_rate: String(formData.affiliate_rate),
            network_rate: String(formData.network_rate ?? ''),
            display_rate: formData.display_rate !== '' && formData.display_rate != null ? String(formData.display_rate) : '',
            display_net_rate: formData.display_net_rate !== '' && formData.display_net_rate != null ? String(formData.display_net_rate) : '',
            display_rate_type: formData.display_rate_type || '',
            offer_type: formData.base_rate_type || ''
        }

        try {
            const res = await callAPI(`affiliate/${formData.affiliate.id}/specialRates/scheduleRateChange`, 'POST', body)
            if (res.success) {
                showSystemMessage('success', res.message || 'Special rate change scheduled.')
                closeModal()
                refetch()
            } else {
                showSystemMessage('error', res.errors?.message || 'Could not schedule special rate change.')
            }
        } catch (error) {
            showSystemMessage('error', error.message)
        } finally {
            setScheduleSubmitting(false)
        }
    }

    useEffect(() => {
        if (formData.rate_names === '' && formData.campaign.id !== 0) {
            setFormData({
                ...formData,
                rate_names: ratenameData.rateNames,
            })
        }
    }, [ratenameData])

    useEffect(() => {
        if (ratename === '') {
            setPendingScheduledRateChange(null)
            return
        }

        let cancelled = false
        ;(async () => {
            const result = await callAPI(`affiliate/${affiliate.id}/${campaign.id}/specialRate?ratename=${ratename}`, 'GET')

            if (cancelled) return

            if (result.success) {
                const { rate } = result
                setPendingScheduledRateChange(result.pending_scheduled_rate_change || null)
                setFormData((prev) => ({
                    ...prev,
                    affiliate_rate: rate.affiliate_rate,
                    network_rate: rate.network_rate,
                    display_rate: rate.display_rate,
                    display_net_rate: rate.display_net_rate,
                    display_rate_type: rate.display_rate_type,
                    base_affiliate_rate: rate.base_affiliate_rate,
                    base_network_rate: rate.base_network_rate,
                    base_display_rate: rate.base_display_rate,
                    base_display_net_rate: rate.base_display_net_rate,
                    base_rate_type: rate.rate_type,
                    hide_sale_amount: rate.hide_sale_amount,
                    override_url: rate.override_url,
                }))
            } else {
                setPendingScheduledRateChange(null)
                showSystemMessage('error', result.errors.message)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [ratename, affiliate.id, campaign.id])

    return (
        <>
            <Modal.Header closeButton>
                <h1 className="mb-h1-dark">Special Rate for: {affiliate.id ? <Affiliate aid={affiliate.id} name={affiliate.name} priority={affiliate.priority.value} status={affiliate.status.value} /> : <Campaign oid={campaign.id} name={campaign.name} />}</h1>
            </Modal.Header>
            <Form onSubmit={saveRate}>
                <Modal.Body>
                    <>
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span>Affiliate</span>
                            <div className="w-75">{affiliate.id ? `${affiliate.name} (${affiliate.id})` : <AffiliateSearch showSystemMessage setFormData={setFormData} />}</div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span>Campaign</span>
                            <div className="w-75">{campaign.id ? <Campaign oid={campaign.id} name={campaign.name} /> : <CampaignSearch setFormData={setFormData} />}</div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span>Special Aff Rate: </span>
                            <div className="w-75 d-flex">
                                <div className="w-50 d-flex align-items-center">
                                    <span className="mx-2">Actual: </span>
                                    <Form.Control
                                        disabled={formData.display_rate > 0 && formData.display_net_rate > 0}
                                        type="text"
                                        value={formData.affiliate_rate}
                                        onChange={(e) => {
                                            setFormData({ ...formData, affiliate_rate: e.target.value })
                                        }}
                                        required
                                    />
                                </div>
                                <div className="w-50 d-flex align-items-center">
                                    <span className="mx-2 text-info font-weight-bold">Display: </span>
                                    <Form.Control
                                        type="text"
                                        value={formData.display_rate ? formData.display_rate : ''}
                                        onChange={(e) => {
                                            setFormData({ ...formData, affiliate_rate: calculateAffiliateRate(formData.affiliate_rate, formData.network_rate, e.target.value, formData.display_net_rate), display_rate: e.target.value })
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {formData.display_rate > 0 && formData.display_net_rate > 0 && (
                            <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                                <span></span>
                                <div className="w-75 d-flex text-secondary">* Will be calculated to match display rate.</div>
                            </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span>Special Net Rate: </span>
                            <div className="w-75 d-flex">
                                <div className="w-50 d-flex align-items-center">
                                    <span className="mx-2">Actual: </span>
                                    <Form.Control
                                        disabled={formData.display_rate > 0 && formData.display_net_rate > 0}
                                        type="text"
                                        value={formData.network_rate}
                                        onChange={(e) => {
                                            setFormData({ ...formData, network_rate: e.target.value })
                                        }}
                                    />
                                </div>
                                <div className="w-50 d-flex align-items-center">
                                    <span className="mx-2 text-info font-weight-bold">Display: </span>
                                    <Form.Control
                                        type="text"
                                        value={formData.display_net_rate ? formData.display_net_rate : ''}
                                        onChange={(e) => {
                                            setFormData({ ...formData, affiliate_rate: calculateAffiliateRate(formData.affiliate_rate, formData.network_rate, formData.display_rate, e.target.value), display_net_rate: e.target.value })
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span>Rate Name: </span>
                            <div className="w-75">
                                {ratename ? (
                                    <div>
                                        <h5>
                                            <div className="badge badge-secondary">{ratename}</div>
                                        </h5>
                                        <div className="border rounded p-1">
                                            Base Actual: ({formData.base_affiliate_rate} vs {formData.base_network_rate}){' '}
                                            {formData.base_display_rate > 0 && (
                                                <i className="font-weight-normal">
                                                    {' '}
                                                    - Base display: ({formData.base_display_rate} vs {formData.base_display_net_rate})
                                                </i>
                                            )}
                                        </div>
                                    </div>
                                ) : ratenameDataLoaded ? (
                                    ratenameData.rateNames.length === 0 ? (
                                        campaign.id ? (
                                            'Affiliate already has special rates for all available rates.'
                                        ) : (
                                            'Please select a campaign.'
                                        )
                                    ) : (
                                        ratenameData.rateNames.map((rateName) => {
                                            if (rateName.status === 'A' || rateName.status === 'H') {
                                                return (
                                                    <div className="border rounded p-1 my-1">
                                                        <Form.Check
                                                            type="checkbox"
                                                            name="rate_names"
                                                            id={rateName.name}
                                                            value={rateName.name}
                                                            onChange={() => {
                                                                if (formData.rate_names.findIndex((e) => e === rateName.name) > -1) {
                                                                    // Remove it
                                                                    setFormData({
                                                                        ...formData,
                                                                        rate_names: formData.rate_names.filter((item) => item !== rateName.name),
                                                                    })
                                                                } else {
                                                                    // Add it
                                                                    if (formData.rate_names.length === 0) {
                                                                        // If this is the first selected, we can also update the fields                                                                        
                                                                        setFormData({
                                                                            ...formData,
                                                                            rate_names: [...formData.rate_names, rateName.name],
                                                                            affiliate_rate: rateName.affiliate_rate_value,
                                                                            network_rate: rateName.network_rate_value,
                                                                            display_rate: rateName.display_rate_value,
                                                                            display_net_rate: rateName.display_net_rate_value,
                                                                            display_rate_type: rateName.display_offer_type == 'F' || rateName.display_offer_type == 'P' ? rateName.display_offer_type : '',
                                                                            hide_sale_amount: rateName.hide_sale_amount == 'Y' ? true : false,
                                                                        })
                                                                    } else {
                                                                        // There's more than 1
                                                                        setFormData({
                                                                            ...formData,
                                                                            rate_names: [...formData.rate_names, rateName.name],
                                                                        })
                                                                    }
                                                                }
                                                            }}
                                                            label={
                                                                <label className={`ml-2 my-0 ${rateName.default_flag === 'Y' && 'font-weight-bold'}`} htmlFor={rateName.name}>
                                                                    {rateName.status === 'H' ? <FontAwesomeIcon className="text-secondary" icon={faEyeSlash} /> : ''} {rateName.name} - ({rateName.affiliate_rate} vs {rateName.network_rate}) {rateName.default_flag === 'Y' && '[default]'}{' '}
                                                                    {rateName.display_rate_value > 0 && (
                                                                        <i className="font-weight-normal">
                                                                            <br />
                                                                            &nbsp;&nbsp;displayed: ({rateName.display_rate} vs {rateName.display_net_rate})
                                                                        </i>
                                                                    )}
                                                                </label>
                                                            }
                                                            checked={formData.rate_names.includes(rateName.name)}
                                                        />
                                                    </div>
                                                )
                                            }
                                        })
                                    )
                                ) : null}
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span>Override URL: </span>
                            <div className="w-75">
                                <Form.Control
                                    type="text"
                                    value={formData.override_url}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            override_url: e.target.value,
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-header text-info">Display Rate Settings</div>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                                    <span style={{ width: '20%' }}>Rate Type: </span>
                                    <div className="w-75">
                                        <Form.Control as="select" value={formData.display_rate_type} onChange={(e) => setFormData({ ...formData, display_rate_type: e.target.value })}>
                                            <option value=""></option>
                                            <option value="F">$ per lead</option>
                                            <option value="P">% of sale</option>
                                            <option value="N">% of commission</option>
                                            {/* {campaign.type.value === 'A' && (
                                                <>
                                                    <option value="M">$ aff / % net</option>
                                                    <option value="C">$ per click</option>
                                                </>
                                            )} */}
                                        </Form.Control>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                                    <span style={{ width: '20%' }}>Hide Sale Amount: </span>
                                    <div className="w-75">
                                        <Form.Check type="checkbox" checked={formData.hide_sale_amount} onChange={(e) => setFormData({ ...formData, hide_sale_amount: e.target.checked })} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                </Modal.Body>
                <Modal.Footer>
                    {ratename && pendingScheduledRateChange && <ScheduledRateChangeBanner jobDate={pendingScheduledRateChange.job_date} affiliateRate={pendingScheduledRateChange.affiliate_rate} networkRate={pendingScheduledRateChange.network_rate} displayRate={pendingScheduledRateChange.display_rate} displayNetRate={pendingScheduledRateChange.display_net_rate} currency={campaign.currency} rateType={(parseInt(pendingScheduledRateChange.display_rate) > 0 && parseInt(pendingScheduledRateChange.display_net_rate) > 0) ? formData.display_rate_type : formData.base_rate_type} />}
                    {ratename ? (
                        isScheduleMode ? (
                            <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '0.5rem' }}>
                                <span className="mb-0 mr-2">Change rate on:</span>
                                <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: '200px' }}>
                                    <DatePicker
                                        id="special_rate_schedule_date_time"
                                        placeholderText="Select Date & Time"
                                        className="form-control"
                                        selected={scheduleDate}
                                        minDate={moment().toDate()}
                                        showTimeSelect
                                        dateFormat="yyyy/MM/dd HH:mm:ss"
                                        onChange={(d) => {
                                            const el = document.getElementById('special_rate_schedule_date_time')
                                            if (el) el.setCustomValidity('')
                                            setScheduleDate(d)
                                        }}
                                        autoComplete="off"
                                    />
                                    <Calendar className="ml-1 flex-shrink-0" size={16} color="#007bff" />
                                </div>
                                <div className="d-flex ml-auto" style={{ gap: '0.5rem' }}>
                                    <Button
                                        variant="outline-danger"
                                        type="button"
                                        disabled={scheduleSubmitting}
                                        onClick={() => {
                                            setIsScheduleMode(false)
                                            setScheduleDate(null)
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="button" disabled={scheduleSubmitting} onClick={handleScheduleSpecialRateChange}>
                                        {scheduleSubmitting ? 'Scheduling…' : 'Schedule'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex justify-content-end w-100" style={{ gap: '0.5rem' }}>
                                <Button variant="warning" type="button" onClick={() => setIsScheduleMode(true)}>
                                    Schedule Rate Change
                                </Button>
                                <CustomButton variant="primary" type="submit" name="Save" spin={submitting} />
                            </div>
                        )
                    ) : (
                        <CustomButton variant="primary" type="submit" name="Save" spin={submitting} />
                    )}
                </Modal.Footer>
            </Form>
        </>
    )
}

export default SpecialRatesModal
